import React, { useCallback, useState } from 'react';
import './Styles.css';
import { areFieldsValid, getChangedFieldsForAgent, getEmployer, mapEmployerListForSelect, onSubmit } from './Functions';
import ChannelDataHandler from './ChannelDataHandler';
import { formatMessage } from '../../../../../Localization/formatMessage';
import Messages from '../../../../../Localization/Messages';
import { useGlobalContext } from '../../../../../context/GlobalContext';
import { BusinessArea } from '../../../../../Enums/BusinessArea';
import { CommissionModel } from '../../../../../Enums/CommissionModel';
import AgentInfoButtons from './Buttons';
import { PresentationMode } from '../../../../../Enums/PresentationMode';
import { getCommonInputFields, getErrorMessage, getValueForInputChange } from '../../../../../Helpers/FormInputHelper';
import TextInput from '../../../../../Elements/ids/InputElements/TextInput';
import Form from '../../../../../Elements/Form';
import CheckBox from '../../../../../Elements/ids/InputElements/CheckBox';
import ErrorMessage from '../../../../../Elements/ErrorMessage';
import { useNavigate } from 'react-router-dom';
import { canAgentNameBeSet, canCommissionEnabledBeSet, canEmployerBeSet } from '../../../Functions';
import { useDebouncedEffect, useLegalUnitName } from '../../../../../Helpers/CustomHooks';
import Select from '../../../../../Elements/ids/InputElements/Select';
import DraggableModal from '../../../../../Elements/DraggableModal';

export const agentInfoFormId = 'agentInfoForm';

const AgentInfo = ({ closeView, agent, team, teamPageDispatch, areBrokerFunctionsAvailable, doesTeamExist }) => {
  const navigate = useNavigate();
  const [editedAgent, setEditedAgent] = useState({
    ...agent,
    costCenterId: agent?.costCenter?.id,
    // Derive commissionModel from commissionEnabled for backward compat with old API responses
    commissionModel: agent?.commissionModel ?? (agent?.commissionEnabled ? CommissionModel.AssurandorIIF : null),
  });
  const [modalMode, setModalMode] = useState(
    Object.keys(agent).length === 0 ? PresentationMode.Add : PresentationMode.Edit
  );
  const [canSubmit, setCanSubmit] = useState(false);
  const [errors, setErrors] = useState([]);
  const { businessArea } = useGlobalContext();
  const legalUnitName = useLegalUnitName(team);

  const getCommonFields = (propertyName, isMandatory) => {
    return getCommonInputFields(editedAgent, setEditedAgent, propertyName, errors, isMandatory);
  };

  const closeError = () => {
    setErrors([]);
    setModalMode(Object.keys(agent).length === 0 ? PresentationMode.Add : PresentationMode.Edit);
  };

  const validateAgentInfo = useCallback(() => {
    const validationErrors = areFieldsValid(editedAgent, canAgentNameBeSet(legalUnitName));
    setErrors(validationErrors);
    setCanSubmit(
      validationErrors.length === 0
        ? modalMode !== PresentationMode.Add
          ? getChangedFieldsForAgent(editedAgent, agent) > 0
          : true
        : false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedAgent]);

  useDebouncedEffect(validateAgentInfo, 500);

  return (
    <DraggableModal
      titleText={agent.code}
      closeView={closeView}
      buttons={
        <AgentInfoButtons closeView={closeView} canSubmit={canSubmit} modalMode={modalMode} closeError={closeError} />
      }>
      <div className="team-page_agent-info_container">
        <Form
          formId={agentInfoFormId}
          onSubmit={e => {
            onSubmit(
              e,
              team,
              editedAgent,
              modalMode,
              setModalMode,
              doesTeamExist,
              agent,
              setErrors,
              setCanSubmit,
              closeView,
              navigate,
              teamPageDispatch
            );
          }}>
          <ChannelDataHandler editedAgent={editedAgent} setEditedAgent={setEditedAgent} />

          {canAgentNameBeSet(legalUnitName) && (
            <TextInput
              cssClasses="team-page_agent-info_name"
              label={formatMessage(Messages.common_name)}
              {...getCommonFields('name', true)}
            />
          )}

          <TextInput
            cssClasses="team-page_agent-info_email"
            label={formatMessage(Messages.common_email)}
            {...getCommonFields('email', false)}
          />

          <TextInput
            cssClasses="team-page_agent-info_phone"
            label={formatMessage(Messages.common_phone)}
            {...getCommonFields('phone', false)}
          />

          <TextInput
            cssClasses="team-page_agent-info_phone"
            label={formatMessage(Messages.common_mobile)}
            {...getCommonFields('mobile', false)}
          />

          <TextInput
            cssClasses="team-page_agent-info_externalSalesCode"
            label={formatMessage(Messages.common_externalSalesCode)}
            {...getCommonFields('externalSalesCode', false)}
          />

          {(businessArea ?? businessArea) === BusinessArea.Commercial && (
            <TextInput
              cssClasses="team-page_agent-info_additionalName"
              label={formatMessage(Messages.common_additionalName)}
              {...getCommonFields('additionalName', false)}
            />
          )}

          {canEmployerBeSet(team.employers) && (
            <Select
              label={formatMessage(Messages.agentInfo_employer)}
              defaultValue={editedAgent.employer?.id}
              options={mapEmployerListForSelect(team.employers)}
              onChange={e => {
                setEditedAgent({ ...editedAgent, employer: getEmployer(team.employers, e.target.value) });
              }}
            />
          )}

          {canCommissionEnabledBeSet(legalUnitName) && (
            <>
              <Select
                label={formatMessage(Messages.agentInfo_commissionModel)}
                defaultValue={editedAgent.commissionModel ?? ''}
                options={[
                  { key: 'none', value: '', displayName: '' },
                  {
                    key: CommissionModel.AssurandorIIF,
                    value: CommissionModel.AssurandorIIF,
                    displayName: formatMessage(Messages.agentInfo_commissionModel_AssurandorIIF),
                  },
                  {
                    key: CommissionModel.ExternalSalesDK,
                    value: CommissionModel.ExternalSalesDK,
                    displayName: formatMessage(Messages.agentInfo_commissionModel_externalSalesDK),
                  },
                ]}
                onChange={e => {
                  const value = e.target.value === '' ? null : e.target.value;
                  setEditedAgent({
                    ...editedAgent,
                    commissionModel: value,
                    // TODO (post-release US 221125): Remove commissionEnabled once API no longer requires it
                    commissionEnabled: value === CommissionModel.AssurandorIIF,
                  });
                }}
              />

              <TextInput
                label={formatMessage(Messages.agentInfo_employeeNumber)}
                {...getCommonFields('employeeNumber', editedAgent.commissionModel === CommissionModel.AssurandorIIF)}
                cssClasses="teamPage-agentInfo-employeeNumber"
              />
            </>
          )}

          {areBrokerFunctionsAvailable && (
            <>
              <TextInput
                cssClasses="team-page_agent-info_email"
                label={formatMessage(Messages.agentInfo_groupEmail)}
                {...getCommonFields('groupEmail', false)}
                value={editedAgent.extension?.groupEmail}
                onChange={e => {
                  setEditedAgent({
                    ...editedAgent,
                    extension: {
                      ...editedAgent.extension,
                      groupEmail: getValueForInputChange(editedAgent.extension?.groupEmail, e.target.value),
                    },
                  });
                }}
              />

              <CheckBox
                cssClasses="team-page_agent-info_checkboxes"
                label={formatMessage(Messages.agentInfo_digital)}
                onChange={() => {
                  setEditedAgent({
                    ...editedAgent,
                    extension: {
                      ...editedAgent.extension,
                      isDigitalCustomer: editedAgent.extension?.isDigitalCustomer
                        ? !editedAgent.extension?.isDigitalCustomer
                        : true,
                    },
                  });
                }}
                checked={editedAgent.extension?.isDigitalCustomer}
                checkBoxId={'isDigitalCustomer'}
              />

              <CheckBox
                cssClasses="team-page_agent-info_checkboxes"
                label={formatMessage(Messages.agentInfo_xmlExport)}
                onChange={() => {
                  setEditedAgent({
                    ...editedAgent,
                    extension: {
                      ...editedAgent.extension,
                      availableForXmlExport: editedAgent.extension?.availableForXmlExport
                        ? !editedAgent.extension.availableForXmlExport
                        : true,
                    },
                  });
                }}
                checked={editedAgent.extension?.availableForXmlExport}
                checkBoxId={'availableForXmlExport'}
              />
            </>
          )}

          <CheckBox
            cssClasses="team-page_agent-info_checkboxes"
            label={formatMessage(Messages.agentInfo_canBePortfolioResponsible)}
            onChange={() => {
              setEditedAgent({
                ...editedAgent,
                canBePortfolioResponsible: !editedAgent.canBePortfolioResponsible,
              });
            }}
            checked={editedAgent.canBePortfolioResponsible}
            checkBoxId={'canBePortfolioResponsible'}
          />

          <ErrorMessage message={getErrorMessage(agentInfoFormId, errors)} />
        </Form>
      </div>
    </DraggableModal>
  );
};

export default AgentInfo;
